import {CommonModule} from '@angular/common';
import {Component, Input, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {formatRecordedAt, resolveRecordedBy} from '../../../shared/commons/audit-display.util';
import {PostcodeDto} from './postcode-service';
import {DistrictDto, SubDistrictDto} from '../../../../generated-model/models';
import {DistrictAutocompleteComponent} from '../../../shared/commons/district-autocomplete/district-autocomplete';
import {
  SubdistrictAutocompleteComponent
} from '../../../shared/commons/subdistrict-autocomplete/subdistrict-autocomplete';

@Component({
  selector: 'app-postcode-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DistrictAutocompleteComponent, SubdistrictAutocompleteComponent],
  templateUrl: './postcode-modal.html'
})
export class PostcodeModal implements OnInit {
  @Input() postcode: PostcodeDto | undefined;
  @Input() viewOnly = false;
  @Input() onSave?: (payload: PostcodeDto, isEditMode: boolean) => Promise<boolean>;

  form: FormGroup;
  isEditMode = false;
  submitted = false;
  isSubmitting = false;
  recordedByDisplay = '-';
  recordedAtDisplay = '-';
  subdistrictDisplayText: string | null = null;
  districtDisplayText: string | null = null;
  provinceDisplayText: string | null = null;

  constructor(public activeModal: NgbActiveModal, private fb: FormBuilder) {
    this.form = this.fb.group({
      id: [null],
      postcode: ['', [Validators.required, Validators.maxLength(255)]],
      subdistrictCode: ['', [Validators.required, Validators.maxLength(255)]],
      districtCode: ['', [Validators.required, Validators.maxLength(255)]],
    });
  }

  ngOnInit(): void {
    if (this.postcode) {
      this.isEditMode = true;
      this.form.patchValue(this.postcode);
      this.subdistrictDisplayText = this.postcode.subdistrictDisplay ?? null;
      this.districtDisplayText = this.postcode.districtDisplay ?? null;
      this.provinceDisplayText = this.postcode.provinceDisplay ?? null;
      this.recordedByDisplay = resolveRecordedBy(this.postcode);
      this.recordedAtDisplay = formatRecordedAt(this.postcode);
    } else {
      this.form.reset();
      this.subdistrictDisplayText = null;
      this.districtDisplayText = null;
      this.provinceDisplayText = null;
    }

    if (this.viewOnly) {
      this.form.disable();
    }
  }

  save(): void {
    if (this.viewOnly) {
      return;
    }
    this.submitted = true;
    if (this.form.invalid) {
      return;
    }

    const payload = this.form.getRawValue() as PostcodeDto;
    void this.submitAndClose(payload);
  }

  onSubdistrictSelected(item: SubDistrictDto): void {
    this.subdistrictDisplayText = this.formatSubdistrictDisplay(item);
    const district = item.district ?? null;
    const province = item.province ?? null;
    if (district?.code) {
      this.districtDisplayText = this.formatDistrictDisplay(district);
      this.form.patchValue({
        districtCode: district.code,
      }, {emitEvent: false});
    }
    this.provinceDisplayText = province ? this.formatProvinceDisplay(province) : this.provinceDisplayText;
  }

  onDistrictSelected(item: DistrictDto): void {
    this.districtDisplayText = this.formatDistrictDisplay(item);
    this.provinceDisplayText = item.province ? this.formatProvinceDisplay(item.province) : this.provinceDisplayText;
  }

  private async submitAndClose(payload: PostcodeDto): Promise<void> {
    if (!this.onSave) {
      this.activeModal.close(payload);
      return;
    }

    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    try {
      const isSuccess = await this.onSave(payload, this.isEditMode);
      if (isSuccess) {
        this.activeModal.close(payload);
      }
    } finally {
      this.isSubmitting = false;
    }
  }

  private formatSubdistrictDisplay(item: SubDistrictDto): string {
    const code = item.code ?? '-';
    const title = item.title ?? '';
    return `${title}(${code})`.trim();
  }

  private formatDistrictDisplay(item: DistrictDto): string {
    const code = item.code ?? '-';
    const title = item.title ?? '';
    return `${title}(${code})`.trim();
  }

  private formatProvinceDisplay(item: DistrictDto['province']): string {
    const code = item?.code ?? '-';
    const title = item?.title ?? '';
    return `${title}(${code})`.trim();
  }
}
